//@ts-check

/**
 * @fileoverview usePaymentValidation - Custom hook for payment form validation logic
 *
 * @description
 * Extracts all validation logic from PaymentForm component into a reusable hook.
 * Handles validation for card payments (Paytrace/Multipass), ACH payments, and addresses.
 */

/** @constant Payment gateway identifiers */
const PAYMENT_GATEWAYS = {
  PAYTRACE: 'paytrace',
  MULTIPASS: 'multipass',
};

/** @constant Tab indices for payment method selection */
const PAYMENT_TABS = {
  CARD: 0,
  ACH: 1,
};

/**
 * @description Custom hook providing payment form validation functions
 * @param {ValidationDependencies} deps - Dependencies for validation
 * @returns {{ validateForm: (errors: CardErrors) => boolean }} Validation functions
 */
export const usePaymentValidation = (deps) => {
  const {
    paymentGateway,
    activeTab,
    card,
    ach,
    firstName,
    email,
    invoiceNumber,
    amount,
    calculationResult,
    tsepResponse,
    setEmailField,
    setInvoiceNumberField,
    setAmountField,
    achFormRef,
    billingAddressFormValidationRef,
    shippingAddressFormValidationRef,
    setCardErrors,
    setFieldErrors,
    setErrors,
  } = deps;

  /**
   * @description Validate card fields for Paytrace gateway
   * @param {CardData} cardData - Card form data
   * @param {CardErrors} currentCardErrors - Current card error state
   * @returns {{ errors: string[], cardErrors: CardErrors }}
   */
  const validateCardFields = (cardData, currentCardErrors) => {
    const validationErrors = [];
    const updatedCardErrors = { ...currentCardErrors };

    const isCardNumberInvalid = cardData.cardNumber.includes('_') || !cardData.cardNumber;
    const isExpiryInvalid = cardData.cardExpiryDate.includes('_') || cardData.cardExpiryDate === '__/____';
    const isCscInvalid = cardData.cardCsc.includes('_') || cardData.cardCsc === '___';

    if (isCardNumberInvalid) {
      updatedCardErrors.cardNumber = true;
      validationErrors.push('Please enter a valid card number.');
    }
    if (isExpiryInvalid) {
      updatedCardErrors.cardExpiryDate = true;
      validationErrors.push('Please enter a valid expiry date.');
    }
    if (isCscInvalid) {
      updatedCardErrors.cardCsc = true;
      validationErrors.push('Please enter a valid CVV.');
    }

    // Validate expiry date month
    const month = cardData.cardExpiryDate.split('/')[0];
    const monthNum = Number.parseInt(month, 10);
    if (month && month.length === 2 && (monthNum <= 0 || monthNum > 12)) {
      updatedCardErrors.cardExpiryDate = true;
      validationErrors.push('Invalid card expiry date.');
    }

    return { errors: validationErrors, cardErrors: updatedCardErrors };
  };

  /**
   * @description Validate calculation result exists
   * @returns {string[]} Validation errors
   */
  const validateCalculationResult = () => {
    // @ts-ignore - calculationResult is null initially but always set before validation
    if (!calculationResult?.totalAmount) {
      return ['Total has not been calculated.'];
    }
    return [];
  };

  /**
   * @description Validate first name field
   * @returns {{ errors: string[], fieldErrors: Object }}
   */
  const validateFirstName = () => {
    if (!firstName.trim()) {
      return {
        errors: ['Please enter a first name.'],
        fieldErrors: { firstName: true },
      };
    }
    return { errors: [], fieldErrors: {} };
  };

  /**
   * @description Conditionally validate email field (only if setter exists - field is managed by parent)
   * @returns {{ errors: string[], fieldErrors: Object }}
   */
  const validateEmail = () => {
    if (!setEmailField) {
      return { errors: [], fieldErrors: {} };
    }

    if (!email || !email.trim()) {
      return {
        errors: ['Please enter an email address.'],
        fieldErrors: { email: true },
      };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return {
        errors: ['Please enter a valid email address.'],
        fieldErrors: { email: true },
      };
    }

    return { errors: [], fieldErrors: {} };
  };

  /**
   * @description Conditionally validate invoice number field (only if setter exists)
   * @returns {{ errors: string[], fieldErrors: Object }}
   */
  const validateInvoiceNumber = () => {
    if (!setInvoiceNumberField) {
      return { errors: [], fieldErrors: {} };
    }

    if (!invoiceNumber || !invoiceNumber.trim()) {
      return {
        errors: ['Please enter an invoice number.'],
        fieldErrors: { invoiceNumber: true },
      };
    }

    return { errors: [], fieldErrors: {} };
  };

  /**
   * @description Conditionally validate amount field (only if setter exists)
   * @returns {{ errors: string[], fieldErrors: Object }}
   */
  const validateAmount = () => {
    if (!setAmountField) {
      return { errors: [], fieldErrors: {} };
    }

    if (!amount || Number(amount) <= 0) {
      return {
        errors: ['Please enter a valid amount greater than 0.'],
        fieldErrors: { amount: true },
      };
    }

    return { errors: [], fieldErrors: {} };
  };

  /**
   * @description Get TSEP expiration date from response or DOM
   * @returns {string} Expiration date or empty string
   */
  const getTsepExpirationDate = () => {
    let expirationDate = tsepResponse?.expirationDate;

    if (!expirationDate || expirationDate === '__/____') {
      const datepickerDiv = document.getElementById('tsep-datepickerDiv');
      if (datepickerDiv) {
        const inputField = datepickerDiv.querySelector('input');
        expirationDate = inputField?.value;
      }
    }

    return expirationDate || '';
  };

  /**
   * @description Validate TSEP (Multipass) encrypted card data and expiration date
   * @returns {string[]} Validation errors
   */
  const validateTsepEncryption = () => {
    const errors = [];
    const encryptedCard = tsepResponse?.tsepToken || localStorage.getItem('0');
    const encryptedCSC = tsepResponse?.cvv2 || localStorage.getItem('1');

    if (!encryptedCard || !encryptedCSC) {
      errors.push('Card encryption failed. Please try again.');
    } else {
      const expirationDate = getTsepExpirationDate();
      if (!expirationDate || expirationDate === '__/____' || expirationDate?.trim() === '') {
        errors.push('Please enter a valid card expiration date.');
      }
    }

    return errors;
  };

  /**
   * @description Validate card payment tab (Paytrace or Multipass)
   * @param {CardErrors} currentCardErrors - Current card error state
   * @returns {{ errors: string[], cardErrors: CardErrors, fieldErrors: Object }}
   */
  const validateCardPaymentTab = (currentCardErrors) => {
    let errors = [];
    let cardErrors = { ...currentCardErrors };
    let fieldErrors = {};

    // Validate first name
    const firstNameValidation = validateFirstName();
    errors.push(...firstNameValidation.errors);
    fieldErrors = { ...fieldErrors, ...firstNameValidation.fieldErrors };

    // Validate conditional fields (only if their setters exist)
    const emailValidation = validateEmail();
    errors.push(...emailValidation.errors);
    fieldErrors = { ...fieldErrors, ...emailValidation.fieldErrors };

    const invoiceValidation = validateInvoiceNumber();
    errors.push(...invoiceValidation.errors);
    fieldErrors = { ...fieldErrors, ...invoiceValidation.fieldErrors };

    const amountValidation = validateAmount();
    errors.push(...amountValidation.errors);
    fieldErrors = { ...fieldErrors, ...amountValidation.fieldErrors };

    // Gateway-specific validation
    if (paymentGateway === PAYMENT_GATEWAYS.PAYTRACE) {
      const cardValidation = validateCardFields(card, cardErrors);
      errors.push(...cardValidation.errors);
      cardErrors = cardValidation.cardErrors;
      // Paytrace not encrypted yet
    } else if (paymentGateway === PAYMENT_GATEWAYS.MULTIPASS) {
      errors.push(...validateTsepEncryption());
    }

    return { errors, cardErrors, fieldErrors };
  };

  /**
   * @description Validate ACH payment tab fields
   * @returns {string[]} Validation errors
   */
  const validateAchPaymentTab = () => {
    const errors = [];

    if (!ach.accountNumber?.trim() || !ach.routingNumber?.trim()) {
      errors.push('Please fill in all required ACH fields (Account Number and Routing Number).');
    } else if (achFormRef.current && !achFormRef.current.validate()) {
      errors.push('Please fill in all required ACH fields.');
    }

    return errors;
  };

  /**
   * @description Validate billing and shipping address fields using refs
   * @returns {string[]} Validation errors
   */
  const validateAddressFields = () => {
    let isAddressValid = true;

    if (billingAddressFormValidationRef.current) {
      isAddressValid = isAddressValid && billingAddressFormValidationRef.current.validate();
    }
    if (shippingAddressFormValidationRef.current) {
      isAddressValid = isAddressValid && shippingAddressFormValidationRef.current.validate();
    }

    if (!isAddressValid) {
      return ['Please complete all required address fields.'];
    }

    return [];
  };

  /**
   * @description Validate entire form before submission - orchestrates focused validators
   * @param {CardErrors} cardErrors - Current card errors state
   * @returns {boolean} True if form is valid
   */
  const validateForm = (cardErrors) => {
    let allErrors = [];
    let allFieldErrors = {};
    let newCardErrors = { ...cardErrors };

    // Validate calculation result
    allErrors.push(...validateCalculationResult());

    // Validate payment method specific fields
    if (activeTab === PAYMENT_TABS.CARD) {
      const cardValidation = validateCardPaymentTab(newCardErrors);
      allErrors.push(...cardValidation.errors);
      newCardErrors = cardValidation.cardErrors;
      allFieldErrors = { ...allFieldErrors, ...cardValidation.fieldErrors };
    } else if (activeTab === PAYMENT_TABS.ACH) {
      allErrors.push(...validateAchPaymentTab());
    }

    // Validate address fields
    allErrors.push(...validateAddressFields());

    // Update state with validation results
    setFieldErrors(allFieldErrors);
    setCardErrors(newCardErrors);
    setErrors(allErrors);

    return allErrors.length === 0;
  };

  return {
    validateForm,
  };
};

export default usePaymentValidation;

/** @typedef {{ cardNumber: boolean, cardExpiryDate: boolean, cardCsc: boolean }} CardErrors */
/** @typedef {{ cardNumber: string, cardExpiryDate: string, cardCsc: string }} CardData */
/** @typedef {{ tsepToken?: string, cvv2?: string, expirationDate?: string, cardType?: string, status?: string, cardNum?: string, message?: string }} TsepResponseData */
/** @typedef {{ validate: () => boolean }} FormRef */
/** @typedef {{ accountName?: string, accountNumber?: string, routingNumber?: string, bankAccountType?: string, bankName?: string }} AchData */

/**
 * @typedef {Object} ValidationDependencies
 * @property {string} paymentGateway - Current payment gateway
 * @property {number} activeTab - Active payment tab
 * @property {CardData} card - Card form data
 * @property {AchData} ach - ACH form data
 * @property {string} firstName - First name value
 * @property {string|null} [email] - Email value (optional, only validated if setter exists)
 * @property {string|null} [invoiceNumber] - Invoice number value (optional, only validated if setter exists)
 * @property {string|number|null} [amount] - Amount value (optional, only validated if setter exists)
 * @property {any} calculationResult - Fee calculation result
 * @property {TsepResponseData} tsepResponse - TSEP response data
 * @property {Function|null} [setEmailField] - Email setter function (field only validated if exists)
 * @property {Function|null} [setInvoiceNumberField] - Invoice number setter function (field only validated if exists)
 * @property {Function|null} [setAmountField] - Amount setter function (field only validated if exists)
 * @property {React.RefObject<FormRef>} achFormRef - ACH form validation ref
 * @property {React.RefObject<FormRef>} billingAddressFormValidationRef - Billing address validation ref
 * @property {React.RefObject<FormRef>} shippingAddressFormValidationRef - Shipping address validation ref
 * @property {Function} setCardErrors - Set card errors state
 * @property {Function} setFieldErrors - Set field errors state
 * @property {Function} setErrors - Set errors state
 */
